import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ComplianceGroupSettings, GroupRequirements } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Button, Card, CheckboxField, EmptyState, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';
import { COMPLIANCE } from './api';
import { useDocumentTypes } from './ComplianceTypesPage';

type Mode = 'refuse' | 'warn';
type Choice = Mode | 'none';

/** "a@x.com, b@y.com" -> list; blanks dropped. */
export function parseEmails(text: string): string[] {
  return text
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function RequirementsCard({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const types = useDocumentTypes();
  const key = ['w18', 'compliance', 'requirements', groupId];
  const req = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get(`${COMPLIANCE}/groups/${groupId}/requirements`, {
        schema: GroupRequirements,
      }),
  });
  const [draft, setDraft] = useState<Record<string, Choice>>({});
  useEffect(() => {
    if (req.data) setDraft(Object.fromEntries(req.data.requirements.map((r) => [r.typeId, r.mode])));
  }, [req.data]);
  const save = useMutation({
    mutationFn: () => {
      const requirements = Object.entries(draft)
        .filter(([, mode]) => mode !== 'none')
        .map(([typeId, mode]) => ({ typeId, mode: mode as Mode }));
      return api.put(`${COMPLIANCE}/groups/${groupId}/requirements`, {
        body: { requirements },
        schema: GroupRequirements,
      });
    },
    onSuccess: async (r) => {
      qc.setQueryData(key, r);
      toast(t('common.saved'));
      await qc.invalidateQueries({ queryKey: ['w18', 'compliance', 'status'] });
    },
  });
  if (req.isError) return <ErrorPanel error={req.error} onRetry={() => void req.refetch()} />;
  if (!req.data || !types.data) return <Spinner />;
  const stored = Object.fromEntries(req.data.requirements.map((r) => [r.typeId, r.mode]));
  const current = (id: string): Choice => draft[id] ?? 'none';
  const changed = types.data.some((d) => current(d.id) !== (stored[d.id] ?? 'none'));
  const active = types.data.filter((d) => d.active || stored[d.id]);
  return (
    <Card
      title={t('w18.compGroup.title')}
      description={t('w18.compGroup.description')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      {active.length === 0 ? (
        <EmptyState message={t('w18.compGroup.noTypes')} />
      ) : (
        <div className="space-y-3">
          {active.map((d) => (
            <SelectField key={d.id} label={d.name} value={current(d.id)} disabled={!canEdit} onChange={(e) => setDraft({ ...draft, [d.id]: e.target.value as Choice })}>
              <option value="none">{t('w18.compGroup.mode.none')}</option>
              <option value="refuse">{t('w18.compGroup.mode.refuse')}</option>
              <option value="warn">{t('w18.compGroup.mode.warn')}</option>
            </SelectField>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs text-slate-600">{t('w18.compGroup.modeHelp')}</p>
      <ErrorLine error={save.error} />
    </Card>
  );
}

function GroupSettingsCard({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const { query, save, values } = useScopedSettings(`/groups/${groupId}/settings`, 'compliance');
  const [draft, setDraft] = useState<{
    acceptPendingDocuments: boolean;
    selfUpload: boolean;
    emails: string;
  } | null>(null);
  useEffect(() => {
    if (!values) return;
    const s = ComplianceGroupSettings.parse(values);
    setDraft({
      acceptPendingDocuments: s.acceptPendingDocuments,
      selfUpload: s.selfUpload,
      emails: s.reminderEmails.join(', '),
    });
  }, [values]);
  if (query.isError) return <ErrorPanel error={query.error} onRetry={() => void query.refetch()} />;
  if (!values || !draft) return <Spinner />;
  const s = ComplianceGroupSettings.parse(values);
  const emails = parseEmails(draft.emails);
  const changed = draft.acceptPendingDocuments !== s.acceptPendingDocuments || draft.selfUpload !== s.selfUpload || emails.join(',') !== s.reminderEmails.join(',');
  return (
    <Card
      title={t('w18.compGroup.settings.title')}
      footer={
        canEdit ? (
          <Button
            variant="primary"
            disabled={!changed || save.isPending}
            onClick={() =>
              save.mutate({
                acceptPendingDocuments: draft.acceptPendingDocuments,
                selfUpload: draft.selfUpload,
                reminderEmails: emails,
              })
            }
          >
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <CheckboxField
          label={t('w18.compGroup.selfUpload')}
          hint={t('w18.compGroup.selfUploadHint')}
          checked={draft.selfUpload}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...draft, selfUpload: e.target.checked })}
        />
        <CheckboxField
          label={t('w18.compGroup.acceptPending')}
          hint={t('w18.compGroup.acceptPendingHint')}
          checked={draft.acceptPendingDocuments}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...draft, acceptPendingDocuments: e.target.checked })}
        />
        <TextField
          label={t('w18.compGroup.reminderEmails')}
          hint={t('w18.compGroup.reminderEmailsHint')}
          value={draft.emails}
          disabled={!canEdit}
          onChange={(e) => setDraft({ ...draft, emails: e.target.value })}
        />
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}

/**
 * Group card "Compliance" (contract compliance s6): which documents the group must hold, refuse or warn, plus the group's
 * upload and reminder settings. Exported for the group page (W23).
 */
export function ComplianceGroupCard({ scopeId, canEdit }: SettingsCardProps) {
  return (
    <div className="space-y-6">
      <RequirementsCard groupId={scopeId} canEdit={canEdit} />
      <GroupSettingsCard groupId={scopeId} canEdit={canEdit} />
    </div>
  );
}
